import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/home/Footer";
import Navbar from "../components/common/Navbar";
import MetaTags from '../components/MetaTags';
import { tourSchemas } from '../data/structuredData';

const Tours: React.FC = () => {
  // Add state for active filter category
  const [activeCategory, setActiveCategory] = useState<string>("all");

  // Form state for the booking widget
  const [formData, setFormData] = useState({
    tourName: "",
    preferredDate: "",
    numGuests: "",
    userEmail: ""
  });

  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Reference to the form element
  const formRef = useRef<HTMLFormElement>(null);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission with Formspree
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      const response = await fetch("https://formspree.io/f/xkgjvqwe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tourName: formData.tourName,
          preferredDate: formData.preferredDate,
          numGuests: formData.numGuests,
          userEmail: formData.userEmail
        }),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          tourName: "",
          preferredDate: "",
          numGuests: "",
          userEmail: ""
        });
      } else {
        throw new Error("Server responded with an error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError("Failed to send your booking request. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Combined structured data for tours listing page
  const toursPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Safari Tours | Rion Wildlife Safaris",
    "description": "Explore our premium safari tours in Namibia, Botswana and Victoria Falls led by Professor Rion Haraeb. From 9-day wildlife expeditions to 19-day Big Five adventures.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": tourSchemas.map((tour, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": tour.name,
          "description": tour.description,
          "offers": tour.offers,
          "url": `https://rionwildlifesafaris.com/tours/${tour.name.toLowerCase().replace(/\s+/g, '-')}`
        }
      }))
    }
  };

  // Rion's safari tours data based on client documentation
  const safariTours = [
    {
      id: 1,
      title: "Namibia Highlights Safari",
      location: "Namibia",
      duration: "14 days",
      price: "€3,600",
      imageSrc: "/tour-images/namibia-highlights.jpeg",
      highlights: "Windhoek, Kalahari Desert, Sossusvlei UNESCO dunes, Swakopmund, Etosha National Park",
      route: "/tours/namibia-highlights" // This links to our newly created page
    },
    {
      id: 2,
      title: "Walvisbay Excursions",
      location: "Namibia",
      duration: "6+ hours",
      price: "€80",
      imageSrc: "/tour-images/walvisbay.jpeg",
      highlights: "Pink Lake, Dune 7, Welwitschia, Moonlandscape (Photography focused)",
      route: "/tours/walvisbay-excursion" // Link to our new page
    },
    {
      id: 3,
      title: "Namwild Safari",
      location: "Namibia",
      duration: "9 days",
      price: "€1,900",
      imageSrc: "/tour-images/namwild.jpeg",
      highlights: "Windhoek, Swakopmund, Etosha (South and North), Monte Etjo",
      route: "/tours/namwild-safari" // This links to our newly created page
    },
    {
      id: 4,
      title: "Namibia, Botswana, Victoria Falls",
      location: "Multi-country",
      duration: "18 days",
      price: "€4,500",
      imageSrc: "/tour-images/victoria-falls.jpeg",
      highlights: "Chobe, Okavango Delta, and Victoria Falls highlights",
      route: "/tours/namibia-botswana-victoria-falls" // Updated to match the App.tsx route
    },
    {
      id: 5,
      title: "Big Five Safari (Nambovic)",
      location: "Namibia & Botswana",
      duration: "19 days",
      price: "€4,800",
      imageSrc: "/tour-images/bigfive.jpeg",
      highlights: "Etosha, Chobe, Big Five wildlife spotting",
      route: "/tours/big-five-safari" // This links to our newly created page
    }
  ];



  // Function to determine tour category
  const getTourCategory = (tour: any): string[] => {
    const categories = [];

    // Wildlife Safaris (most safari tours)
    if (tour.duration.includes("days") && tour.highlights.toLowerCase().includes("etosha")) {
      categories.push("wildlife");
    }

    // Day Excursions (shorter tours)
    if (tour.duration.includes("hours") || tour.title.toLowerCase().includes("excursion")) {
      categories.push("excursion");
    }

    // Multi-country
    if (tour.location.includes("&") || tour.location === "Multi-country") {
      categories.push("multi-country");
    }

    // Photography Tours
    if (tour.highlights.toLowerCase().includes("photography") ||
      tour.title.toLowerCase().includes("walvisbay") ||
      tour.title.toLowerCase().includes("photo")) {
      categories.push("photography");
    }

    return categories;
  };

  // Filter tours based on active category
  const filteredTours = safariTours.filter(tour => {
    if (activeCategory === "all") return true;

    const tourCategories = getTourCategory(tour);
    return tourCategories.includes(activeCategory);
  });

  return (
    <>
      <MetaTags
        title="Safari Tours | Rion Wildlife Safaris"
        description="Explore our premium safari tours in Namibia, Botswana and Victoria Falls led by Professor Rion Haraeb. From 9-day wildlife expeditions to 19-day Big Five adventures."
        canonicalUrl="https://rionwildlifesafaris.com/tours"
        structuredData={toursPageStructuredData}
        ogType="website"
      />

      <div className="bg-white">
        {/* Hero Section with improved styling */}
        <div
          className="relative h-[65vh] min-h-[500px] flex items-center justify-center mt-0 overflow-hidden"
        >
          <img
            src="/images/hero/hero-background.jpeg"
            alt="Rion Wildlife Safaris Tours"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/50 pointer-events-none"></div>
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>

          {/* Navbar - absolutely positioned at top */}
          <header className="absolute top-0 left-0 right-0 z-50">
            <Navbar />
          </header>

          {/* Hero content */}
          <div className="relative z-10 text-center text-white px-4 max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-up">Our Safari Tours</h1>
            <p className="text-xl max-w-3xl mx-auto mb-8 animate-fade-up animation-delay-200">Experience the best of African wildlife with our premium safari packages.</p>
          </div>

          {/* Bottom gradient */}
          <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
        </div>

        {/* White background container for all content below hero - matching imported file structure */}
        <div className="w-full bg-white">
          <div className="py-8 md:py-12 lg:py-16 container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {/* Tours List - Takes 2/3 of the width on large screens */}
              <div className="lg:col-span-2">
                {/* Horizontal category selection bar - improved for mobile */}
                <div className="mb-6 overflow-x-auto scrollbar-hide">
                  <div className="flex space-x-2 pb-2">
                    <button
                      className={`px-4 sm:px-5 py-2 ${activeCategory === "all" ? "bg-[#C05B2A] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"} font-medium text-sm sm:text-base rounded-full whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                      onClick={() => setActiveCategory("all")}
                    >
                      All Tours
                    </button>
                    <button
                      className={`px-4 sm:px-5 py-2 ${activeCategory === "wildlife" ? "bg-[#C05B2A] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"} font-medium text-sm sm:text-base rounded-full whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                      onClick={() => setActiveCategory("wildlife")}
                    >
                      Wildlife Safaris
                    </button>
                    <button
                      className={`px-4 sm:px-5 py-2 ${activeCategory === "excursion" ? "bg-[#C05B2A] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"} font-medium text-sm sm:text-base rounded-full whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                      onClick={() => setActiveCategory("excursion")}
                    >
                      Day Excursions
                    </button>
                    <button
                      className={`px-4 sm:px-5 py-2 ${activeCategory === "multi-country" ? "bg-[#C05B2A] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"} font-medium text-sm sm:text-base rounded-full whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                      onClick={() => setActiveCategory("multi-country")}
                    >
                      Multi-country
                    </button>
                    <button
                      className={`px-4 sm:px-5 py-2 ${activeCategory === "photography" ? "bg-[#C05B2A] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800"} font-medium text-sm sm:text-base rounded-full whitespace-nowrap flex-shrink-0 transition-colors duration-200`}
                      onClick={() => setActiveCategory("photography")}
                    >
                      Photography Tours
                    </button>
                  </div>
                </div>

                <div className="space-y-4 md:space-y-6">
                  {filteredTours.length > 0 ? (
                    filteredTours.map((tour) => (
                      <div key={tour.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
                        <div className="flex flex-col md:flex-row">
                          <div className="w-full md:w-48 h-48 flex-shrink-0 aspect-video md:aspect-square">
                            <img
                              src={tour.imageSrc}
                              alt={tour.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/images/placeholder.jpg';
                              }}
                            />
                          </div>
                          <div className="p-4 md:p-5 flex flex-col justify-between w-full">
                            <div>
                              <h3 className="text-lg md:text-xl font-bold text-[#3D3E48] mb-2">{tour.title}</h3>
                              <div className="flex items-center text-[#3D3E48] mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm">{tour.location}</span>
                              </div>
                              <p className="text-[#3D3E48] text-sm mb-4">{tour.highlights}</p>
                            </div>
                            <div className="flex flex-wrap justify-between items-center gap-3">
                              <div className="text-[#3D3E48]">
                                <span className="font-bold">{tour.price}</span>
                                <span className="text-sm ml-1">| {tour.duration}</span>
                              </div>
                              <Link
                                to={tour.route}
                                className="inline-block px-4 py-2 text-sm font-bold text-white bg-[#C05B2A] rounded-[40px]"
                                onClick={() => window.scrollTo(0, 0)}
                              >
                                View Tour
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="bg-white rounded-lg shadow-md p-8 text-center">
                      <p className="text-lg text-gray-600">No tours found in this category.</p>
                      <button
                        className="mt-4 px-5 py-2 bg-[#C05B2A] text-white font-medium rounded-full"
                        onClick={() => setActiveCategory("all")}
                      >
                        View All Tours
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Tour Booking Widget - 1/3 column */}
              <div className="mt-8 lg:mt-0">
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 shadow-md sticky top-24">
                  <h2 className="text-xl font-bold text-[#3D3E48] mb-4">Book Your Safari Tour</h2>

                  {submitSuccess ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md mb-4">
                      <p className="font-medium">Thank you for your booking request!</p>
                      <p className="text-sm mt-1">We'll contact you shortly to confirm your safari tour details.</p>
                      <button
                        onClick={() => setSubmitSuccess(false)}
                        className="mt-3 text-sm text-green-700 underline hover:text-green-800"
                      >
                        Submit another request
                      </button>
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#3D3E48] mb-1">Select Tour</label>
                        <select
                          name="tourName"
                          value={formData.tourName}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C05B2A]"
                          required
                        >
                          <option value="">-- Select a Tour --</option>
                          {safariTours.map(tour => (
                            <option key={tour.id} value={tour.title}>{tour.title}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3D3E48] mb-1">Preferred Date</label>
                        <input
                          type="date"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C05B2A]"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3D3E48] mb-1">Number of Guests</label>
                        <input
                          type="number"
                          name="numGuests"
                          value={formData.numGuests}
                          onChange={handleInputChange}
                          min="1"
                          placeholder="Enter number of guests"
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C05B2A]"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3D3E48] mb-1">Your Email</label>
                        <input
                          type="email"
                          name="userEmail"
                          value={formData.userEmail}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C05B2A]"
                          required
                        />
                      </div>

                      {submitError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                          {submitError}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="w-full px-4 py-3 bg-[#C05B2A] text-white font-bold rounded-md hover:bg-[#A04B20] transition duration-300 flex justify-center items-center"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          "Request Booking"
                        )}
                      </button>
                    </form>
                  )}

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-bold text-[#3D3E48] mb-3">Need Help?</h3>
                    <p className="text-sm text-gray-600 mb-4">Contact Professor Rion directly to discuss your safari tour options and availability.</p>
                    <a
                      href="/contact"
                      className="inline-block w-full text-center px-4 py-2 border-2 border-[#C05B2A] text-[#C05B2A] font-bold rounded-md hover:bg-[#C05B2A] hover:text-white transition duration-300"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section (equivalent to TourFAQ in the imported file) */}
          <div className="bg-gray-50 py-8 md:py-12">
            <div className="container mx-auto px-4 sm:px-6">
              <h2 className="text-2xl font-bold text-[#3D3E48] mb-6 md:mb-8 text-center">Frequently Asked Questions</h2>
              <div className="max-w-3xl mx-auto space-y-4 md:space-y-6">
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
                  <h3 className="text-lg font-bold text-[#3D3E48] mb-2">What is included in the tour price?</h3>
                  <p className="text-gray-600">All our safari tours include accommodation, transportation, guided game drives, entrance fees to national parks, and most meals as specified in the itinerary.</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
                  <h3 className="text-lg font-bold text-[#3D3E48] mb-2">How do I book a tour?</h3>
                  <p className="text-gray-600">You can book directly through our website or contact Professor Rion Haraeb directly. A 70% deposit is required to secure your booking.</p>
                </div>
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
                  <h3 className="text-lg font-bold text-[#3D3E48] mb-2">What is the best time to visit Namibia?</h3>
                  <p className="text-gray-600">The dry season (May to October) is generally considered the best time for wildlife viewing, as animals gather around water sources and vegetation is less dense.</p>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Tours; 